import type { WeddingPropType } from 'components/PublicWebsite/pages/Home/types';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { WebsitePreviewWeddingValues } from 'reducers/website/websitePreview';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export enum WeddingDateDisplayFormatEnum {
  EXACT = 'EXACT',
  RANGE = 'DATE_RANGE',
  CUSTOM = 'CUSTOM',
}

export type CustomDateType = {
  displayFormat: WeddingDateDisplayFormatEnum.CUSTOM;
  customDateMessage?: string;
};

export type WebsiteDateRangeType<T extends Dayjs | string | undefined> = {
  displayFormat: WeddingDateDisplayFormatEnum.RANGE;
  weddingDateRangeStart?: T;
  weddingDateRangeEnd?: T;
};

export type WebsiteExactDateType<T extends Dayjs | string | undefined> = {
  displayFormat: WeddingDateDisplayFormatEnum.EXACT;
  weddingDate?: T;
};

export type WebsiteDatePublicPropType<T extends Dayjs | string | undefined> =
  | CustomDateType
  | WebsiteDateRangeType<T>
  | WebsiteExactDateType<T>;

type GetReturnDateType<T> = T extends string ? string : Dayjs;

export const mapWeddingAccountToWebsiteDateProp = <T extends string | undefined>(
  weddingView: WeddingPropType,
  dateFormat: T
): WebsiteDatePublicPropType<GetReturnDateType<T>> => {
  const {
    custom_date_message: customDateMessage,
    wedding_date, // "2022-05-06"
    wedding_date_range_start, // Date
    wedding_date_range_end, // Date
  } = weddingView;

  const formatDate = (d?: string | Date): GetReturnDateType<typeof dateFormat> | undefined => {
    if (d) {
      if (dateFormat) {
        return dayjs.utc(d).format(dateFormat) as GetReturnDateType<typeof dateFormat>;
      }
      return dayjs.utc(d) as GetReturnDateType<typeof dateFormat>;
    }
    return undefined;
  };

  if (!wedding_date) {
    return { displayFormat: WeddingDateDisplayFormatEnum.EXACT, weddingDate: undefined };
  }

  if (customDateMessage) {
    return { displayFormat: WeddingDateDisplayFormatEnum.CUSTOM, customDateMessage };
  }
  if (wedding_date_range_start && wedding_date_range_end) {
    return {
      displayFormat: WeddingDateDisplayFormatEnum.RANGE,
      weddingDateRangeStart: formatDate(wedding_date_range_start),
      weddingDateRangeEnd: formatDate(wedding_date_range_end),
    };
  }
  return {
    displayFormat: WeddingDateDisplayFormatEnum.EXACT,
    weddingDate: formatDate(wedding_date),
  };
};

type GetReturnPreviewDateType<D, T extends string | undefined> = D extends undefined
  ? undefined
  : GetReturnDateType<T>;

export const mapWebsitePreviewWeddingValuesToWebsiteDateProp = <T extends string | undefined>(
  websitePreviewWeddingValues: WebsitePreviewWeddingValues,
  dateFormat: T
): WebsiteDatePublicPropType<GetReturnDateType<T>> | undefined => {
  const {
    date,
    weddingDateDisplayFormat,
    weddingDateRangeStart,
    weddingDateRangeEnd,
    customDateMessage,
  } = websitePreviewWeddingValues;
  if (!date) return undefined;

  const formatDate = (
    d?: string | Date,
    inputDateFormat?: string
  ): undefined | GetReturnDateType<typeof dateFormat> => {
    if (!d) return undefined;
    if (dateFormat) {
      return dayjs.utc(d, inputDateFormat).format(dateFormat) as GetReturnPreviewDateType<
        typeof d,
        typeof dateFormat
      >;
    }
    return dayjs.utc(d, inputDateFormat) as GetReturnPreviewDateType<typeof d, typeof dateFormat>;
  };

  if (weddingDateDisplayFormat === WeddingDateDisplayFormatEnum.CUSTOM) {
    return { displayFormat: WeddingDateDisplayFormatEnum.CUSTOM, customDateMessage };
  }
  if (weddingDateDisplayFormat === WeddingDateDisplayFormatEnum.RANGE) {
    return {
      displayFormat: WeddingDateDisplayFormatEnum.RANGE,
      weddingDateRangeStart: formatDate(weddingDateRangeStart, 'MM/DD/YYYY'),
      weddingDateRangeEnd: formatDate(weddingDateRangeEnd, 'MM/DD/YYYY'),
    };
  }
  return {
    displayFormat: WeddingDateDisplayFormatEnum.EXACT,
    weddingDate: formatDate(date, 'YYYY-MM-DD'),
  };
};
